import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    TableSortLabel,
} from '@mui/material';
import { Close as CloseIcon, Stars as PointsIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { memberService } from '../services/memberService';
import { useAuth } from '../contexts/AuthContext';
import userNameService from '../services/userNameService';

const MemberComplementaryPointsModal = ({ open, onClose, membershipNumber, memberName, onSuccess }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    
    // Past complementary points data
    const [pastPoints, setPastPoints] = useState([]);
    
    // User names mapping (email -> name)
    const [userNames, setUserNames] = useState({});
    
    // Form data
    const [points, setPoints] = useState('');
    const [description, setDescription] = useState('');
    
    // Sorting state
    const [orderBy, setOrderBy] = useState('HandoutDate');
    const [order, setOrder] = useState('desc');

    // Tier advancement dialog state
    const [tierAdvancementOpen, setTierAdvancementOpen] = useState(false);
    const [tierAdvancementInfo, setTierAdvancementInfo] = useState(null);

    // Fetch past complementary points when modal opens
    useEffect(() => {
        if (open && membershipNumber) {
            fetchData();
        }
    }, [open, membershipNumber]);

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setPoints('');
            setDescription('');
            setError(null);
            setSuccess(null);
        }
    }, [open]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch past complementary points
            const response = await memberService.getMemberComplementaryPoints(membershipNumber);

            const complementaryPoints = response.complementaryPoints || [];
            setPastPoints(complementaryPoints);

            // Fetch user names for all staff members
            if (complementaryPoints.length > 0) {
                const staffEmails = [...new Set(complementaryPoints.map(p => p.Staff))]; // Unique emails
                const names = await userNameService.fetchUserNames(staffEmails);
                setUserNames(names);
            }
        } catch (err) {
            console.error('Error fetching complementary points:', err);
            setError(err.response?.data?.error || 'Failed to fetch complementary points data');
        } finally {
            setLoading(false);
        }
    };

    const handleHandout = async () => {
        // Validate form
        if (!points || points.trim() === '' || isNaN(points)) {
            setError('Please enter a valid number of points');
            return;
        }

        const pointsValue = parseInt(points);
        if (pointsValue <= 0) {
            setError('Points must be greater than 0');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            setSuccess(null);

            const response = await memberService.handoutComplementaryPoints({
                memberNo: membershipNumber,
                points: pointsValue,
                description: description.trim() || null,
                handoutDate: new Date().toISOString()
            });

            // Debug: Log the response to see the structure
            console.log('Complementary Points Response:', response);
            console.log('Response data:', response.data);
            console.log('TiersAdvanced:', response.data?.TiersAdvanced);
            
            // Extract tier advancement info from response
            const tiersAdvanced = response.data?.TiersAdvanced || 0;
            console.log('Tiers Advanced (extracted):', tiersAdvanced);

            // Check if tiers were advanced
            if (tiersAdvanced > 0) {
                console.log('Showing tier advancement dialog!');
                // Show tier advancement dialog
                setTierAdvancementInfo({
                    tiersAdvanced: tiersAdvanced,
                    currentTier: response.data.CurrentTier,
                    totalPoints: response.data.TotalPoints,
                    pointsToNextTier: response.data.PointsToNextTier
                });
                setTierAdvancementOpen(true);
                
                // Reset form
                setPoints('');
                setDescription('');
                
                // Refresh past points list
                await fetchData();
                
                // Don't call onSuccess() yet - wait for user to acknowledge tier advancement
            } else {
                console.log('No tier advancement (TiersAdvanced:', tiersAdvanced, ')');
                setSuccess('Complementary points handed out successfully!');
                
                // Reset form
                setPoints('');
                setDescription('');
                
                // Refresh past points list
                await fetchData();
                
                // Call onSuccess callback to refresh parent component
                if (onSuccess) {
                    onSuccess();
                }
            }

        } catch (err) {
            console.error('Error handing out complementary points:', err);
            setError(err.response?.data?.error || 'Failed to hand out complementary points');
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleTierAdvancementClose = () => {
        setTierAdvancementOpen(false);
        setSuccess('Complementary points handed out successfully!');
        setTierAdvancementInfo(null);
        
        // Now refresh parent component after user acknowledged the tier advancement
        if (onSuccess) {
            onSuccess();
        }
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortData = (data) => {
        return [...data].sort((a, b) => {
            let aValue = a[orderBy];
            let bValue = b[orderBy];

            // Handle date sorting
            if (orderBy === 'HandoutDate') {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            // Handle numeric sorting
            if (orderBy === 'Points') {
                aValue = Number(aValue);
                bValue = Number(bValue);
            }

            // Handle string comparison (case-insensitive)
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const sortedPoints = sortData(pastPoints);

    // Calculate total points
    const totalPoints = pastPoints.reduce((sum, point) => sum + (point.Points || 0), 0);

    return (
        <>
            <Dialog 
                open={open} 
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '600px',
                        maxHeight: '90vh'
                    }
                }}
            >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                pb: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PointsIcon color="primary" />
                    <Typography variant="h6" component="span">
                        Complementary Points - {memberName}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Past Complementary Points Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                                Past Complementary Points
                            </Typography>
                            
                            {pastPoints.length === 0 ? (
                                <Box sx={{ 
                                    p: 3, 
                                    textAlign: 'center', 
                                    backgroundColor: 'grey.50', 
                                    borderRadius: 1 
                                }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No complementary points have been given to this member yet
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: 'grey.100' }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'Points'}
                                                            direction={orderBy === 'Points' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('Points')}
                                                        >
                                                            Points
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'Description'}
                                                            direction={orderBy === 'Description' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('Description')}
                                                        >
                                                            Description
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'Staff'}
                                                            direction={orderBy === 'Staff' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('Staff')}
                                                        >
                                                            Staff
                                                        </TableSortLabel>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>
                                                        <TableSortLabel
                                                            active={orderBy === 'HandoutDate'}
                                                            direction={orderBy === 'HandoutDate' ? order : 'asc'}
                                                            onClick={() => handleRequestSort('HandoutDate')}
                                                        >
                                                            Date
                                                        </TableSortLabel>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {sortedPoints.map((point, index) => (
                                                    <TableRow key={point.ID || index} hover>
                                                        <TableCell>
                                                            <Typography 
                                                                sx={{ 
                                                                    fontWeight: 600, 
                                                                    color: 'primary.main' 
                                                                }}
                                                            >
                                                                {point.Points}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{point.Description || 'N/A'}</TableCell>
                                                        <TableCell>{userNames[point.Staff] || point.Staff}</TableCell>
                                                        <TableCell>{formatDate(point.HandoutDate)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        p: 1,
                                        backgroundColor: 'primary.light',
                                        borderRadius: 1
                                    }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                                            Total Entries: {pastPoints.length}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.contrastText' }}>
                                            Total Points: {totalPoints}
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Complementary Points Handout Section */}
                        <Box>
                            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
                                Give Complementary Points
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                                    {success}
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {/* Points Input */}
                                <TextField
                                    type="number"
                                    label="Points *"
                                    value={points}
                                    onChange={(e) => setPoints(e.target.value)}
                                    fullWidth
                                    disabled={submitting}
                                    placeholder="Enter number of points to give..."
                                    inputProps={{ 
                                        min: 1,
                                        step: 1
                                    }}
                                    helperText="Enter the number of complementary points to give to this member"
                                />

                                {/* Description/Reason */}
                                <TextField
                                    label="Description / Reason (Optional)"
                                    multiline
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    fullWidth
                                    disabled={submitting}
                                    placeholder="Enter the reason for these complementary points (optional)..."
                                    inputProps={{ maxLength: 500 }}
                                    helperText={`${description.length}/500 characters`}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button 
                    onClick={onClose} 
                    variant="outlined"
                    disabled={submitting}
                >
                    Close
                </Button>
                <Button 
                    onClick={handleHandout} 
                    variant="contained"
                    disabled={submitting || loading}
                    startIcon={submitting ? <CircularProgress size={20} /> : <PointsIcon />}
                >
                    {submitting ? 'Processing...' : 'Give Points'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Tier Advancement Dialog - Blocking */}
        <Dialog
            open={tierAdvancementOpen}
            onClose={(event, reason) => {
                // Prevent closing by clicking backdrop or pressing ESC
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
                handleTierAdvancementClose();
            }}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                backgroundColor: 'success.main',
                color: 'success.contrastText'
            }}>
                <TrophyIcon sx={{ fontSize: 40 }} />
                <Typography variant="h5" component="span" sx={{ fontWeight: 600 }}>
                    Tier Advanced!
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ mt: 3 }}>
                {tierAdvancementInfo && (
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                🎉 Congratulations!
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {memberName} has advanced {tierAdvancementInfo.tiersAdvanced} tier{tierAdvancementInfo.tiersAdvanced > 1 ? 's' : ''}!
                            </Typography>
                        </Alert>
                        
                        <Box sx={{ 
                            p: 2, 
                            backgroundColor: 'grey.50', 
                            borderRadius: 1,
                            mb: 2
                        }}>
                            <Typography variant="body1" gutterBottom>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    New Tier:
                                </Box>
                                {' '}
                                <Box component="span" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>
                                    {tierAdvancementInfo.currentTier || 'N/A'}
                                </Box>
                            </Typography>
                            
                            <Divider sx={{ my: 1.5 }} />
                            
                            <Typography variant="body1" gutterBottom>
                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    Total Points:
                                </Box>
                                {' '}
                                <Box component="span" sx={{ fontWeight: 700 }}>
                                    {tierAdvancementInfo.totalPoints?.toLocaleString() || '0'}
                                </Box>
                            </Typography>
                            
                            {tierAdvancementInfo.pointsToNextTier > 0 && (
                                <Typography variant="body1">
                                    <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Points to Next Tier:
                                    </Box>
                                    {' '}
                                    <Box component="span" sx={{ fontWeight: 700 }}>
                                        {tierAdvancementInfo.pointsToNextTier?.toLocaleString()}
                                    </Box>
                                </Typography>
                            )}
                            
                            {tierAdvancementInfo.pointsToNextTier === 0 && (
                                <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    ⭐ Maximum tier reached!
                                </Typography>
                            )}
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            This member has unlocked new benefits and rewards!
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button 
                    onClick={handleTierAdvancementClose}
                    variant="contained"
                    color="success"
                    size="large"
                    fullWidth
                    sx={{ fontWeight: 600 }}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default MemberComplementaryPointsModal;

